import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import type { MomentInput } from "moment";
import moment from "moment";
import axios from "axios";
import DOMPurify from "dompurify";
import { getToken } from "../../../utils/getToken";
import clsx from "clsx";
import _ from "lodash";
import {
  DATE_FORMAT,
  DATE_FORMAT_WITH_TIME,
  DATE_FORMAT_WITH_TIME_LTS,
  TIME_FORMAT,
} from "@packages/shared/constants";
import { Lozenge } from "@packages/ui/primitives/Lozenge";
import type { LozengeVariant } from "@packages/ui/primitives/Lozenge";
import {
  CommaFormatted,
  convertNumberWithCommas,
  convertNumberWithCommasPreserve,
  setDashAsEmptyValue,
} from "@packages/shared/utils";
import { Tooltip } from "@packages/ui/composites/Tooltip";
import styled from "styled-components";

import type { CellRenderProps, CellRendererPlugin } from "../types";
import { PluginRegistry } from "../PluginRegistry";
import { builtInEditorPlugins } from "./editors";
import { useV2TableInternal } from "../../../context/V2TableInternalContext";
import useFileDownloadV2 from "../../../hooks/useFileDownloadV2";

// ─── Shared utilities ─────────────────────────────────────────────────────────
// Alignment is handled by CellRenderer's wrapper div — renderers are pure
// content producers and should NOT apply alignment styles themselves.

const TRUNCATE: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * Shared wrapper that truncates content with ellipsis and shows a DS Tooltip
 * on hover when the content is actually overflowing. Replaces native `title`
 * for consistent tooltip UX across all cell types.
 */
function TruncatedCell({
  display,
  onDoubleClick,
  className,
  style,
  children,
}: {
  display: string;
  onDoubleClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
}) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [display]);

  const content = (
    <div ref={textRef} style={{ ...TRUNCATE, ...style }} className={className}>
      {children ?? display}
    </div>
  );

  return (
    <div onDoubleClick={onDoubleClick}>
      {isTruncated ? (
        <Tooltip.Root side="top" align="start" variant="dark" size="md">
          <Tooltip.Trigger asChild>{content}</Tooltip.Trigger>
          <Tooltip.Content>{display}</Tooltip.Content>
        </Tooltip.Root>
      ) : (
        content
      )}
    </div>
  );
}

const getMaskedValue = (value: string, format: string): string => {
  const len = value.length;
  if (format === "partial") {
    return `XXXXXXXX${value.slice(len - 4, len)}`;
  }
  if (format === "full") {
    return "XXXXXXXXX";
  }
  return value;
};

function stripHtml(html: string): string {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function showDecimalsEnabled(
  format: CellRenderProps["column"]["format"]
): boolean {
  if (format == null || format.decimals === undefined) {
    return false;
  }
  if (typeof format.decimals === "boolean") {
    return format.decimals;
  }
  return true;
}

/** Returns explicit schema precision only — never falls back to ACCEPT_DECIMAL. */
function getDecimalPrecision(
  format: CellRenderProps["column"]["format"]
): number | undefined {
  if (format != null && typeof format.decimals === "number") {
    return format.decimals;
  }
  return undefined;
}

const LEGACY_TO_LOZENGE_VARIANT: Record<string, LozengeVariant> = {
  // Legacy tag variants (from the old Tag component)
  default: "default",
  "default-outline": "default",
  "default-light": "default",
  "default-outline-light": "default",
  primary: "primary",
  "primary-outline": "primary-outline",
  notice: "warning",
  success: "success",
  "success-outline": "success-outline",
  error: "error",
  "error-dark": "error",
  "error-outline": "error-outline",
  info: "info",
  "info-outline": "info-outline",
  "rounded-primary": "primary",
  "rounded-default": "default",
  "pink-tag": "pink",
  // Direct LozengeVariant pass-throughs (new schema API uses these directly)
  warning: "warning",
  pink: "pink",
  "pink-outline": "pink-outline",
};

function toLozengeVariant(v: string): LozengeVariant {
  return LEGACY_TO_LOZENGE_VARIANT[v] ?? "default";
}

function formatTextDisplayValue(
  raw: any,
  column: CellRenderProps["column"]
): string {
  const conditionalValue = column.conditionalValues;
  const format = column.format;
  const fields = column.fields;
  const prefix = column.prefix;
  const ifEmptyText = column.emptyValue ?? "-";
  const fixed = format?.fixed;
  const isDecimalString = fields?.["isDecimalString"] === "true";
  const formatAsNumber = fields?.["formatAsNumber"] === "true";
  const showDecimal = showDecimalsEnabled(format);
  const precision = getDecimalPrecision(format);

  let value = raw;
  if (conditionalValue && value != null) {
    value = conditionalValue[String(value)] ?? value;
  }

  const shouldFormatAsNumber =
    formatAsNumber || showDecimal || typeof value === "number";

  if (
    shouldFormatAsNumber &&
    value !== null &&
    value !== "" &&
    !isNaN(Number(value))
  ) {
    const num = Number(value);
    if (showDecimal) {
      const precision = getDecimalPrecision(format);
      value =
        fixed && precision != null
          ? convertNumberWithCommas(num, true, precision)
          : convertNumberWithCommasPreserve(num);
    } else {
      value = convertNumberWithCommasPreserve(num).split(".")[0];
    }
  }
  if (isDecimalString) {
    const num = Number(value);
    value = _.isFinite(num)
      ? convertNumberWithCommasPreserve(num)
      : ifEmptyText;
  }
  if (format?.commas && value != null && value !== "") {
    value = CommaFormatted(value);
  }
  value = prefix && !_.isEmpty(value) ? `${prefix}${value}` : value;
  value = setDashAsEmptyValue(value);
  value = value ? value : ifEmptyText;
  return String(value);
}

// ─── Text ─────────────────────────────────────────────────────────────────────

function TextCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, onDoubleClick } = props;
  const value = formatTextDisplayValue(props.value, column);
  return (
    <TruncatedCell
      display={value}
      onDoubleClick={onDoubleClick}
      className={clsx(column.uppercase && "uppercase")}
    />
  );
}

// ─── HTML ─────────────────────────────────────────────────────────────────────

function HtmlCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { onDoubleClick } = props;
  const safeHtml = DOMPurify.sanitize(props.value ?? "");
  const plainText = stripHtml(safeHtml);
  return <TruncatedCell display={plainText} onDoubleClick={onDoubleClick} />;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const ModalLink = styled.span`
  cursor: pointer;
  display: inline-block;
  text-decoration: none;
  color: inherit;

  &:hover {
    font-weight: 500;
    color: var(--ui-interactive, #6452D9);
    text-decoration: underline;
  }
`;

function ModalCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { handleShow } = useV2TableInternal();
  const { row, column, value, onDoubleClick } = props;
  const popoverInfo = column.modal ?? {};
  const fields = column.fields ?? {};
  const enableTag =
    (column.modal as { enableTag?: boolean } | null)?.enableTag === true;

  const isExcluded =
    fields["excludeKey"] && fields["excludeValue"]
      ? Array.isArray(fields["excludeValue"])
        ? (fields["excludeValue"] as unknown[]).includes(
          row[fields["excludeKey"]]
        )
        : row[fields["excludeKey"]] === fields["excludeValue"]
      : false;

  const handleClick = (event: MouseEvent<HTMLSpanElement>) => {
    if (handleShow) {
      const idVal = row[fields["id"]];
      handleShow(
        (idVal != null && idVal !== "" ? idVal : row) as never,
        event,
        popoverInfo as never
      );
    }
  };

  const displayText = String(value ?? "-");

  if (isExcluded) {
    return <TruncatedCell display={displayText} onDoubleClick={onDoubleClick} />;
  }

  return (
    <TruncatedCell display={displayText} onDoubleClick={onDoubleClick}>
      <div className="pop-overs" style={{ ...TRUNCATE, position: "relative" }}>
        {enableTag ? (
          <span
            onClick={handleClick}
            style={{
              cursor: "pointer",
              textDecoration: "none",
              color: "black",
              borderWidth: "1px",
              padding: "2px 3px",
              fontSize: "12px",
              backgroundColor: "rgb(243 243 243/ 1)",
              borderRadius: "4px",
            }}
          >
            {value ?? "-"}
          </span>
        ) : (
          <ModalLink onClick={handleClick} className="dt-v2-modal-link pop-overs title">
            {value ?? "-"}
          </ModalLink>
        )}
      </div>
    </TruncatedCell>
  );
}

// ─── Link ─────────────────────────────────────────────────────────────────────

function LinkCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { isLoading, downloadFile } = useFileDownloadV2();
  const { row, column, value, onDoubleClick } = props;
  const url = column.link?.url ?? "";
  const includeParams = column.link?.includeParams ?? true;
  const payloadMapper = column.link?.payloadMapper;
  const documentName = value;
  const id = row["id"] as string | number | undefined;

  const body: Record<string, unknown> = {};
  if (payloadMapper) {
    payloadMapper.forEach((item) => {
      body[item.labelKey] = row[item.valueKey];
    });
  }

  const handleFileClick = () => {
    downloadFile(url, includeParams, String(id ?? ""), body);
  };

  const displayText = String(documentName || "-");

  if (!id) {
    return <TruncatedCell display={displayText} onDoubleClick={onDoubleClick} />;
  }

  return (
    <TruncatedCell display={displayText} onDoubleClick={onDoubleClick}>
      {isLoading ? (
        <span style={{ color: "var(--ui-text-muted, #969696)" }}>Loading…</span>
      ) : (
        <span
          onClick={handleFileClick}
          style={{
            color: "var(--ui-interactive, #6b4d9a)",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {documentName}
        </span>
      )}
    </TruncatedCell>
  );
}

// ─── Date / DateTime / ConditionalDate ────────────────────────────────────────

function DateCellBody({ props, fmt }: { props: CellRenderProps; fmt: string }): JSX.Element {
  const { value, onDoubleClick } = props;
  const m = value ? moment(value as moment.MomentInput) : null;
  const formatted = m && m.isValid() ? m.format(fmt) : "-";
  return <TruncatedCell display={formatted} onDoubleClick={onDoubleClick} />;
}

function ConditionalDateCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, row, value, onDoubleClick } = props;
  const fields = column.fields ?? {};
  const additionalAccessor = row[fields["additionalAccessor"]];
  const rawDate = value || additionalAccessor;
  const m = rawDate ? moment(rawDate as moment.MomentInput) : null;
  const displayValue = m && m.isValid() ? m.format(DATE_FORMAT) : "-";
  return <TruncatedCell display={displayValue} onDoubleClick={onDoubleClick} />;
}

// ─── Badge helpers ────────────────────────────────────────────────────────────

function badgeVariant(
  rawValue: any,
  column: CellRenderProps["column"]
): string {
  const badgeStatus = column.badge?.statusMap;
  const defaultBadgeStatus = column.badge?.defaultVariant;
  if (badgeStatus && rawValue != null && badgeStatus[String(rawValue)]) {
    return badgeStatus[String(rawValue)];
  }
  if (defaultBadgeStatus) {
    return defaultBadgeStatus;
  }
  return "info";
}

// ─── Badge ────────────────────────────────────────────────────────────────────

function renderBadge(
  props: CellRenderProps,
  options: { injectedLabels?: boolean }
): ReactNode {
  const { column, value: rawValue, onDoubleClick } = props;
  let value: any = rawValue;
  const toUpperCase = column.uppercase;
  const conditionalValue = column.conditionalValues;

  if (options.injectedLabels && props.injectedData) {
    const found = props.injectedData.find((item) => item.value === rawValue);
    if (found) {
      value = found.label;
    }
  }

  if (conditionalValue) {
    value = conditionalValue[String(value)] ?? value;
  }

  const displayMap = column.badge?.displayMap;
  if (displayMap != null && rawValue != null) {
    const mapped = displayMap[String(rawValue)];
    if (mapped !== undefined) {
      value = mapped;
    }
  }

  const variant = options.injectedLabels
    ? toLozengeVariant(column.badge?.statusMap?.[String(rawValue)] || "info")
    : toLozengeVariant(badgeVariant(rawValue, column));

  return (
    <div style={{ ...TRUNCATE, display: "inline-flex", maxWidth: "100%" }} onDoubleClick={onDoubleClick}>
      {value ? (
        <Lozenge
          className={clsx(toUpperCase && "uppercase")}
          variant={variant}
          title={String(setDashAsEmptyValue(value))}
          style={{ maxWidth: "100%" }}
        />
      ) : (
        setDashAsEmptyValue(value)
      )}
    </div>
  );
}

// ─── Default Badge ────────────────────────────────────────────────────────────

function renderDefaultBadge(props: CellRenderProps): ReactNode {
  const { column, value: rawValue, onDoubleClick } = props;
  let value: any = rawValue;
  const format = column.format;
  const toUpperCase = column.uppercase;
  const conditionalValue = column.conditionalValues;
  const showDecimal = showDecimalsEnabled(format);
  const precision = getDecimalPrecision(format);

  if (conditionalValue) {
    value = conditionalValue[String(value)] ?? value;
  }
  if (showDecimal) {
    const precision = getDecimalPrecision(format);
    if (format?.fixed && precision != null) {
      const numValue = Number(value);
      value = Number.isInteger(numValue)
        ? numValue
        : numValue.toFixed(precision);
    } else {
      value = convertNumberWithCommasPreserve(value);
    }
  }
  if (format?.commas) {
    value = CommaFormatted(value);
  }

  return (
    <div style={{ ...TRUNCATE, display: "inline-flex", maxWidth: "100%" }} onDoubleClick={onDoubleClick}>
      <Lozenge
        className={clsx(toUpperCase && "uppercase")}
        variant="default"
        title={value}
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
}

// ─── Currency ─────────────────────────────────────────────────────────────────

function CurrencyCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, value, onDoubleClick } = props;
  // Default: preserve API precision (no ACCEPT_DECIMAL pad/truncate).
  // Fixed precision only when schema sets format.decimals to a number.
  const precision = getDecimalPrecision(column.format);
  const amount =
    value == null || value === ""
      ? null
      : precision != null
        ? convertNumberWithCommas(value, true, precision)
        : convertNumberWithCommasPreserve(value);
  const display = amount != null ? `$${amount}` : "-";
  return <TruncatedCell display={display} onDoubleClick={onDoubleClick} />;
}

// ─── Number ───────────────────────────────────────────────────────────────────

function NumberCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, value: rawValue, onDoubleClick } = props;
  const toUpperCase = column.uppercase;
  const format = column.format;
  const conditionalValue = column.conditionalValues;
  const fields = column.fields ?? {};
  const noBadge = fields["noBadge"] === "true";
  const showDecimal = showDecimalsEnabled(format);
  const precision = getDecimalPrecision(format);

  let value: any = rawValue;
  if (conditionalValue) {
    value = conditionalValue[String(value)] ?? value;
  }

  if (value == null) {
    value = setDashAsEmptyValue(value);
  } else {
    if (value !== "") {
      const num = Number(value);
      if (!isNaN(num)) {
        if (showDecimal) {
          const precision = getDecimalPrecision(format);
          value =
            format?.fixed && precision != null
              ? convertNumberWithCommas(num, true, precision)
              : convertNumberWithCommasPreserve(num);
        } else {
          value = convertNumberWithCommasPreserve(num).split(".")[0];
        }
      }
    }
    if (fields["concatText"]) {
      value = `${value} ${fields["concatText"]}`;
    }
    value = setDashAsEmptyValue(value);
  }

  if (noBadge) {
    return (
      <TruncatedCell
        display={String(value)}
        onDoubleClick={onDoubleClick}
        className={clsx(toUpperCase && "uppercase")}
      />
    );
  }

  return (
    <div
      className={clsx(toUpperCase && "uppercase")}
      onDoubleClick={onDoubleClick}
    >
      <Lozenge variant="default" title={value} />
    </div>
  );
}

// ─── Percentage ───────────────────────────────────────────────────────────────

function PercentageCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, value: numValue, onDoubleClick } = props;
  // Fixed precision only when schema sets format.decimals to a number.
  const precision = getDecimalPrecision(column.format);
  const display =
    numValue != null
      ? `${precision != null ? Number(numValue).toFixed(precision) : convertNumberWithCommasPreserve(numValue)}%`
      : "-";
  return <TruncatedCell display={display} onDoubleClick={onDoubleClick} />;
}

// ─── Couple (title + subtitle) ────────────────────────────────────────────────

function CoupleCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, row, rowIndex, onDoubleClick } = props;
  const couple = column.couple;
  if (!couple?.subtitleField) {
    return <></>;
  }
  const titleKey = couple.titleField ?? "";
  const toUpperCase = column.uppercase;
  const enableTag = couple.showTag === true;
  const fields = column.fields ?? {};

  const titleVal = titleKey ? String(row[titleKey] ?? "") : "";
  const subtitleVal = String(row[couple.subtitleField] ?? "");

  return (
    <div
      className={clsx(toUpperCase && "uppercase")}
      style={{ minWidth: 0 }}
      onDoubleClick={onDoubleClick}
    >
      <div style={{ display: "flex", flexDirection: "column", rowGap: 4, minWidth: 0 }}>
        <TruncatedCell
          display={titleVal}
          style={{ lineHeight: "18px" }}
        />
        <div
          id={`cell.${rowIndex}.${couple.subtitleField}`}
          className="cellSubtitle"
          style={{ ...TRUNCATE, fontSize: 12, fontWeight: 500, lineHeight: "14px", color: "var(--ui-text-muted, #717171)" }}
        >
          {enableTag ? (
            <div
              id={`cell.${rowIndex}.${couple.subtitleField}.tag`}
              style={{ display: 'inline-flex', alignItems: 'center' }}
            >
              <span
                style={{
                  position: 'absolute',
                  width: 1,
                  height: 1,
                  overflow: 'hidden',
                  clip: 'rect(0,0,0,0)',
                  whiteSpace: 'nowrap',
                }}
              >
                {fields["subtitlePretext"]
                  ? `${fields["subtitlePretext"]}${subtitleVal}`
                  : subtitleVal}
              </span>
              <Lozenge
                variant={toLozengeVariant(fields["variant"] ?? "default")}
                title={
                  fields["subtitlePretext"]
                    ? `${fields["subtitlePretext"]}${subtitleVal}`
                    : subtitleVal
                }
                style={{ color: "var(--ui-text-muted, #717171)" }}
              />
            </div>
          ) : (
            <TruncatedCell
              display={subtitleVal}
              style={{ fontSize: 12, fontWeight: 500, lineHeight: "14px", color: "var(--ui-text-muted, #717171)" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Couple with Date Range ───────────────────────────────────────────────────

function CoupleWithDateRangeCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, row, onDoubleClick } = props;
  const couple = column.couple;
  const dr = column.dateRange;
  const toUpperCase = column.uppercase;
  if (!couple?.titleField || !dr?.startField || !dr?.endField) {
    return <CoupleCellBody props={props} />;
  }
  const title = row[couple.titleField];
  const startDate = row[dr.startField];
  const endDate = row[dr.endField];

  const formatDateStr = (date: string) => moment(date).format(DATE_FORMAT);

  let dates: string | null = null;
  if (startDate && endDate) {
    dates = `${formatDateStr(String(startDate))} - ${formatDateStr(String(endDate))}`;
  } else if (startDate) {
    dates = formatDateStr(String(startDate));
  } else if (endDate) {
    dates = formatDateStr(String(endDate));
  }

  const titleStr = title != null && title !== "" ? String(title) : "";

  return (
    <div
      className={clsx(toUpperCase && "uppercase")}
      style={{ minWidth: 0 }}
      onDoubleClick={onDoubleClick}
    >
      <div style={{ display: "flex", flexDirection: "column", rowGap: 4, minWidth: 0 }}>
        {titleStr && (
          <TruncatedCell display={titleStr} />
        )}
        {dates && (
          <TruncatedCell display={dates} />
        )}
      </div>
    </div>
  );
}

// ─── Date Range ───────────────────────────────────────────────────────────────

function DateRangeCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { row, onDoubleClick } = props;
  const dr = props.column.dateRange;
  if (!dr) return <></>;
  const display = `${moment(row[dr.startField] as MomentInput).format(
    DATE_FORMAT
  )} - ${moment(row[dr.endField] as MomentInput).format(DATE_FORMAT)}`;
  return <TruncatedCell display={display} onDoubleClick={onDoubleClick} />;
}

// ─── Time ─────────────────────────────────────────────────────────────────────

function TimeCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { value, onDoubleClick } = props;
  const m = value ? moment(value as moment.MomentInput) : null;
  const formatted = m && m.isValid() ? m.format(TIME_FORMAT) : "-";
  return <TruncatedCell display={formatted} onDoubleClick={onDoubleClick} />;
}

// ─── Mask ─────────────────────────────────────────────────────────────────────

function MaskCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, row, onDoubleClick } = props;
  const mask = column.mask;
  if (!mask) return <></>;

  const value = row[mask.valueField];
  const format = mask.format;

  const maskedDisplay = value != null ? getMaskedValue(String(value), format) : "-";
  const fullValue = value != null && value !== "" ? String(value) : "No Data Found";

  return (
    <div onDoubleClick={onDoubleClick}>
      <Tooltip.Root side="top" align="start" variant="dark" size="md">
        <Tooltip.Trigger asChild>
          <div style={{ ...TRUNCATE, cursor: "pointer" }}>
            {maskedDisplay}
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content>{fullValue}</Tooltip.Content>
      </Tooltip.Root>
    </div>
  );
}

// ─── Sensitive Reveal ─────────────────────────────────────────────────────────

const sensitiveRevealCache = new Map<string, string>();

function SensitiveRevealCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, row } = props;
  const config = column.sensitiveReveal;
  if (!config) return <></>;

  const { axiosInstance } = useV2TableInternal();
  const [status, setStatus] = useState<'masked' | 'loading' | 'revealed'>('masked');
  const [revealedValue, setRevealedValue] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const rowId = String(row[config.idField] ?? '');
  const rawValue = row[column.field];
  const placeholder = rawValue != null && String(rawValue).trim() !== ''
    ? getMaskedValue(String(rawValue), config.maskFormat)
    : config.maskType === 'SSN' ? 'xxx-xx-xxxx' : 'xx/xxxx';

  const cacheKey = `${config.endpoint}:${rowId}:${config.responseField}`;

  const handleToggle = useCallback(async () => {
    if (status === 'revealed') {
      setStatus('masked');
      return;
    }

    const cached = sensitiveRevealCache.get(cacheKey);
    if (cached) {
      setRevealedValue(cached);
      setStatus('revealed');
      return;
    }

    if (!rowId) return;

    setStatus('loading');
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      let response: any;
      const endpoint = config.endpoint.replace(/^\//, '');

      if (axiosInstance) {
        if (config.method === 'POST') {
          response = await axiosInstance.post(
            endpoint,
            { [config.paramName]: rowId },
            { signal: controller.signal }
          );
        } else {
          response = await axiosInstance.get(endpoint, {
            params: { [config.paramName]: rowId },
            signal: controller.signal,
          });
        }
      } else {
        const token = getToken();
        const baseUrl = (
          (typeof process !== 'undefined' && process.env?.REACT_APP_WEALTHLANE_API_ENDPOINT) || ''
        ).replace(/\/$/, '');
        const url = config.endpoint.startsWith('http')
          ? config.endpoint
          : `${baseUrl}/${endpoint}`;

        if (config.method === 'POST') {
          response = await axios.post(
            url,
            { [config.paramName]: rowId },
            { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }
          );
        } else {
          response = await axios.get(url, {
            params: { [config.paramName]: rowId },
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
          });
        }
      }

      const data = response.data;
      const extracted = config.responseField.split('.').reduce(
        (acc: any, key: string) => acc?.[key],
        data
      );
      let displayValue = extracted != null ? String(extracted) : '';

      if (config.maskType === 'DOB' && displayValue) {
        const m = moment(displayValue);
        if (m.isValid()) {
          displayValue = m.format(config.dateFormat ?? 'MM/DD/YYYY');
        }
      }

      sensitiveRevealCache.set(cacheKey, displayValue);
      setRevealedValue(displayValue);
      setStatus('revealed');
    } catch (err: any) {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        console.error('[SensitiveReveal] Fetch failed:', err);
        setStatus('masked');
      }
    }
  }, [status, cacheKey, rowId, config, axiosInstance]);

  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  const displayText = status === 'revealed' ? (revealedValue || '-') : placeholder;
  const icon = status === 'loading'
    ? 'progress_activity'
    : status === 'revealed'
      ? 'visibility'
      : 'visibility_off';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%' }}>
      <div style={{ ...TRUNCATE, flex: 1 }} title={status === 'revealed' ? displayText : undefined}>
        {displayText}
      </div>
      <span
        className={`material-symbols-outlined${status === 'loading' ? ' animate-spin' : ''}`}
        style={{ fontSize: '18px', cursor: 'pointer', flexShrink: 0, userSelect: 'none' }}
        onClick={handleToggle}
      >
        {icon}
      </span>
    </div>
  );
}

// ─── Conditional ──────────────────────────────────────────────────────────────

function ConditionalCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { column, value: raw, onDoubleClick } = props;
  const conditionalValue = column.conditionalValues;
  let value: any = raw;
  if (conditionalValue) {
    value = conditionalValue[String(value)] ?? value;
  }
  const display = value != null ? String(value) : "-";
  return <TruncatedCell display={display} onDoubleClick={onDoubleClick} />;
}

// ─── Array Split ──────────────────────────────────────────────────────────────

function ArraySplitCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { value, onDoubleClick } = props;
  const arr = Array.isArray(value) ? value : [];
  const mergeValues = arr.length ? arr.join(", ") : "-";
  return <TruncatedCell display={mergeValues} onDoubleClick={onDoubleClick} />;
}

// ─── Space ────────────────────────────────────────────────────────────────────

function SpaceCellBody({ props }: { props: CellRenderProps }): JSX.Element {
  const { value, onDoubleClick } = props;
  const display = value != null ? String(value) : "";
  return <TruncatedCell display={display} onDoubleClick={onDoubleClick} />;
}

// ─── Registry ─────────────────────────────────────────────────────────────────

export const builtInCellRendererPlugins: CellRendererPlugin[] = [
  {
    type: "text",
    render: (p) => <TextCellBody props={p} />,
  },
  {
    type: "date",
    render: (p) => <DateCellBody props={p} fmt={DATE_FORMAT} />,
  },
  {
    type: "dateTime",
    render: (p) => <DateCellBody props={p} fmt={DATE_FORMAT_WITH_TIME} />,
  },
  {
    type: "dateTimeLTS",
    render: (p) => <DateCellBody props={p} fmt={DATE_FORMAT_WITH_TIME_LTS} />,
  },
  {
    type: "conditionalDate",
    render: (p) => <ConditionalDateCellBody props={p} />,
  },
  {
    type: "badge",
    render: (p) => renderBadge(p, { injectedLabels: false }),
  },
  {
    type: "injectedBadge",
    render: (p) => renderBadge(p, { injectedLabels: true }),
  },
  {
    type: "defaultBadge",
    render: (p) => renderDefaultBadge(p),
  },
  {
    type: "currency",
    render: (p) => <CurrencyCellBody props={p} />,
  },
  {
    type: "number",
    render: (p) => <NumberCellBody props={p} />,
  },
  {
    type: "percentage",
    render: (p) => <PercentageCellBody props={p} />,
  },
  {
    type: "couple",
    render: (p) => <CoupleCellBody props={p} />,
  },
  {
    type: "coupleWithDateRange",
    render: (p) => <CoupleWithDateRangeCellBody props={p} />,
  },
  {
    type: "link",
    render: (p) => <LinkCellBody props={p} />,
  },
  {
    type: "dateRange",
    render: (p) => <DateRangeCellBody props={p} />,
  },
  {
    type: "time",
    render: (p) => <TimeCellBody props={p} />,
  },
  {
    type: "mask",
    render: (p) => <MaskCellBody props={p} />,
  },
  {
    type: "sensitiveReveal",
    render: (p) => <SensitiveRevealCellBody props={p} />,
  },
  {
    type: "conditional",
    render: (p) => <ConditionalCellBody props={p} />,
  },
  {
    type: "modal",
    render: (p) => <ModalCellBody props={p} />,
  },
  {
    type: "html",
    render: (p) => <HtmlCellBody props={p} />,
  },
  {
    type: "arraySplit",
    render: (p) => <ArraySplitCellBody props={p} />,
  },
  {
    type: "space",
    render: (p) => <SpaceCellBody props={p} />,
  },
];

export function createDefaultRegistry(): PluginRegistry {
  const registry = new PluginRegistry();
  registry.registerAll({
    cellRenderers: builtInCellRendererPlugins,
    editors: builtInEditorPlugins,
  });
  return registry;
}
